import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { formatNumber } from './format';

const RackDrawer = ({ run, model, onOpenMaster, selectedBay, outsideLocations = [], onSelectBay, onSelectLocation, onClose }) => {
  if (!run) return null;
  const bay = selectedBay || 1;
  const cells = [...model.cells.values()].filter((cell) => cell.runId === run.id && cell.bay === bay);
  const locations = cells.flatMap((cell) => cell.locations).sort((a, b) => b.placement.level - a.placement.level);
  const shelvingCell = cells.find((cell) => cell.structure === 'shelving');
  const shelvingLocations = shelvingCell?.locations || [];
  const shelvingSection = shelvingLocations[0]?.placement?.section;
  const shelvingBins = shelvingLocations.map((location) => location.placement.bin).sort((a, b) => a - b);
  const outsideSections = outsideLocations.filter((location) => location.placement?.structure === 'shelving').reduce((groups, location) => {
    const section = location.placement.section;
    groups.set(section, [...(groups.get(section) || []), location]);
    return groups;
  }, new Map());
  const runCells = [...model.cells.values()].filter((cell) => cell.runId === run.id);
  const used = runCells.reduce((sum, cell) => sum + cell.used, 0);
  const capacity = runCells.filter((cell) => cell.capacityKnown).reduce((sum, cell) => sum + cell.capacity, 0);

  return (
    <aside className="absolute inset-y-0 right-0 z-30 flex w-[380px] flex-col border-l border-gray-200 bg-white shadow-xl">
      <div className="flex items-start justify-between border-b border-gray-200 px-3 py-2.5">
        <div><p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Warehouse rack</p><h3 className="mt-0.5 text-[16px] font-semibold text-gray-900">{run.label}</h3><p className="mt-0.5 text-xs text-gray-500">{run.bays} bays · {run.heightFt} ft structure</p></div>
        <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><XMarkIcon className="h-4 w-4" /></button>
      </div>
      <div className="thin-scroll flex-1 overflow-y-auto p-3">
        <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
          {capacity ? `${formatNumber(used)} used · ${formatNumber(capacity)} governed capacity` : 'Capacity is manual or unknown for this rack'}
        </div>
        <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Bays</p>
        <div className="mt-1.5 grid grid-cols-6 gap-1">
          {Array.from({ length: run.bays }, (_, index) => (
            <button type="button" key={index + 1} onClick={() => onSelectBay(index + 1)} className={`h-8 rounded-md border text-xs ${bay === index + 1 ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              B{String(index + 1).padStart(2, '0')}
            </button>
          ))}
        </div>
        <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Vertical locations</p>
        {shelvingSection && <p className="mt-1 text-[11px] text-gray-500">Mobile shelving · section B-{String(shelvingSection).padStart(2, '0')} · bins {String(shelvingBins[0]).padStart(2, '0')}-{String(shelvingBins[shelvingBins.length - 1]).padStart(2, '0')}</p>}
        <div className="mt-1.5 overflow-hidden rounded-md border border-gray-200">
          {locations.length ? locations.map((location) => (
            <button type="button" key={location.location_code} onClick={() => onSelectLocation(location)} className="grid w-full grid-cols-[70px_1fr_70px] items-center border-b border-gray-200 px-2 py-2 text-left last:border-b-0 hover:bg-gray-50">
              <span className="text-xs font-medium text-gray-900">Level {location.placement.level}</span>
              <span className="truncate text-[11px] text-gray-500">{location.location_code}</span>
              <span className="text-right text-xs tabular-nums text-gray-600">{formatNumber(location.inventory.reduce((sum, row) => sum + Number(row.quantity || 0), 0))}</span>
            </button>
          )) : <p className="p-3 text-xs text-gray-500">No mapped locations in this bay.</p>}
        </div>
        {outsideSections.size > 0 && <section className="mt-4 border-t border-gray-200 pt-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-red-500">Outside approved structure</p>
          {[...outsideSections.entries()].map(([section, rows]) => <div key={section} className="mt-2">
            <p className="text-xs text-gray-600">B-{String(section).padStart(2, '0')} is shelving section {section}; the approved frame holds sections 01-08 on the B face and 09-13 on the SM face. Needs Bryan&apos;s physical mapping.</p>
            {rows.map((location) => <button type="button" key={location.location_code} onClick={() => onSelectLocation(location)} className="mt-1 block w-full truncate rounded px-1.5 py-1 text-left text-xs text-gray-700 hover:bg-gray-50">{location.location_code}</button>)}
          </div>)}
        </section>}
        <div className="mt-4 border-t border-gray-200 pt-3">
          {run.areas.map((area) => <button type="button" key={area} onClick={() => onOpenMaster?.({ tab: 'locations', area })} className="mr-2 text-xs font-medium text-gray-700 underline hover:text-gray-900">Review {area} in Location Master</button>)}
        </div>
      </div>
    </aside>
  );
};

export default RackDrawer;
